import React, { useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import {
  Box,
  CssBaseline,
  useMediaQuery,
  Backdrop,
  CircularProgress,
  Drawer,
} from '@mui/material';
import { lightTheme, darkTheme } from '../theme/moverTheme';
import EnhancedAppBar from './EnhancedAppBar';
import MoverSidebar from './MoverSidebar';

const APPBAR_HEIGHT = 58;
const SIDEBAR_WIDTH = 184;

const MoverLayout = ({
  children,
  profile,
  token,
  onLogout,
  title,
  loading = false,
  onQuickAction,
}) => {
  const [darkMode, setDarkMode] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const isMobile = useMediaQuery('(max-width:1200px)');

  // Load theme preference from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem('mover-portal-theme');
    if (savedTheme) {
      setDarkMode(savedTheme === 'dark');
    }
  }, [profile]);

  // Save theme preference
  const handleThemeToggle = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('mover-portal-theme', newMode ? 'dark' : 'light');
  };

  const handleMobileToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleQuickAction = (action) => {
    onQuickAction?.(action);
  };

  const currentTheme = darkMode ? darkTheme : lightTheme;

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F8FAFB' }}>
        {/* App Bar */}
        <EnhancedAppBar
          onMenuToggle={handleMobileToggle}
          profile={profile}
          onLogout={onLogout}
          darkMode={darkMode}
          onThemeToggle={handleThemeToggle}
          token={token}
          title={title}
          onQuickAction={handleQuickAction}
        />

        {!isMobile && (
          <MoverSidebar
            profile={profile}
            onLogout={onLogout}
          />
        )}

        <Drawer
          open={isMobile && mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ zIndex: theme => theme.zIndex.drawer + 2 }}
          PaperProps={{ sx: { width: SIDEBAR_WIDTH, bgcolor: '#F8FCFC' } }}
        >
          <MoverSidebar
            profile={profile}
            onLogout={onLogout}
            onNavigate={() => setMobileOpen(false)}
            showCollapse={false}
          />
        </Drawer>

        {isMobile && (
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              backgroundColor: mobileOpen ? 'rgba(15, 23, 42, 0.35)' : 'transparent',
              pointerEvents: mobileOpen ? 'auto' : 'none',
              zIndex: mobileOpen ? 1100 : 0,
              transition: 'background-color 0.2s ease',
            }}
            onClick={() => setMobileOpen(false)}
          />
        )}

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            mt: `${APPBAR_HEIGHT}px`,
            minHeight: `calc(100vh - ${APPBAR_HEIGHT}px)`,
            backgroundColor: '#F8FAFB',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            width: isMobile ? '100%' : 'auto',
            ml: isMobile ? 0 : `${SIDEBAR_WIDTH}px`,
          }}
        >
          {/* Content Container */}
          <Box
            sx={{
              flex: 1,
              p: { xs: 0, sm: 2.5, md: 3 },
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              maxWidth: 1360,
              mx: 'auto',
              overflow: 'hidden',
            }}
            data-tour="dashboard"
          >
            {children}
          </Box>
        </Box>

        {/* Loading Overlay */}
        <Backdrop
          sx={{
            color: '#fff',
            zIndex: theme => theme.zIndex.drawer + 1,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          }}
          open={loading}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress color="primary" size={60} />
            <Box sx={{ textAlign: 'center' }}>
              <div>Loading...</div>
              <div style={{ fontSize: '0.875rem', opacity: 0.8, marginTop: '8px' }}>
                Please wait while we prepare your portal
              </div>
            </Box>
          </Box>
        </Backdrop>
      </Box>
    </ThemeProvider>
  );
};

export default MoverLayout;